import React, { useRef, useState } from 'react';
import { errorMessage } from '../context/AuthContext';

export const fmtSize = (n) => n >= 1048576 ? `${(n / 1048576).toFixed(1).replace('.', ',')} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`;
export const fmtPlanDate = (d) => new Date(String(d).replace(' ', 'T') + 'Z').toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
export const typeLabel = (t) => (t === 'pdf' ? 'PDF' : 'Word');
export const ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Actions admin sur les plans (page publique et page admin) : supprimer un plan, ajouter / retirer des fichiers
export function usePlanActions(authFetch, reload, setFlash) {
  const [busy, setBusy] = useState(null);
  const warnS3 = (d, what) => (d.s3Deleted ? null : `${what} supprimé du site, mais son fichier est resté dans S3 (droit s3:DeleteObject manquant).`);

  const delPlan = async (p) => {
    const n = p.files.length;
    if (!window.confirm(`Supprimer le plan « ${p.title} »${n ? ` et ses ${n} fichier${n > 1 ? 's' : ''}` : ''} ?`)) return;
    setFlash(null);
    try {
      const r = await authFetch(`/api/admin/plans/${p.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(await errorMessage(r));
      const w = warnS3(await r.json(), 'Plan');
      setFlash(w ? { type: 'error', text: w } : { type: 'ok', text: '✓ plan supprimé' });
      reload();
    } catch (e) { setFlash({ type: 'error', text: `Suppression impossible : ${e.message}` }); }
  };

  const delFile = async (p, f) => {
    if (!window.confirm(`Retirer le fichier « ${f.file_name} » du plan « ${p.title} » ?`)) return;
    setFlash(null);
    try {
      const r = await authFetch(`/api/admin/plans/${p.id}/files/${f.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(await errorMessage(r));
      const w = warnS3(await r.json(), 'Fichier');
      setFlash(w ? { type: 'error', text: w } : { type: 'ok', text: '✓ fichier retiré' });
      reload();
    } catch (e) { setFlash({ type: 'error', text: `Suppression impossible : ${e.message}` }); }
  };

  const addFiles = async (p, fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setBusy(p.id); setFlash(null);
    try {
      const fd = new FormData();
      list.forEach(f => fd.append('files', f));
      const r = await authFetch(`/api/admin/plans/${p.id}/files`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await errorMessage(r));
      setFlash({ type: 'ok', text: `✓ ${list.length} fichier${list.length > 1 ? 's' : ''} ajouté${list.length > 1 ? 's' : ''}` });
      setTimeout(() => setFlash(null), 2500);
      reload();
    } catch (e) { setFlash({ type: 'error', text: `Fichiers non ajoutés : ${e.message}` }); }
    finally { setBusy(null); }
  };

  return { busy, delPlan, delFile, addFiles };
}

const fileHref = (f) => `/api/plans/files/${f.id}`;

// Un plan dans la liste : titre, ses fichiers (ouvrir / télécharger) ; actions admin si `actions` est fourni
export function PlanRow({ p, actions }) {
  const input = useRef(null);
  const n = p.files.length;
  const busy = actions && actions.busy === p.id;
  return (
    <li className="note-item plan-item">
      <div className="note-item-meta">
        <span>{fmtPlanDate(p.created_at)}</span>
        <span>{n} fichier{n > 1 ? 's' : ''}</span>
      </div>
      <h3>{n === 1 ? <a href={fileHref(p.files[0])} target="_blank" rel="noopener noreferrer">{p.title}</a> : p.title}</h3>
      {n > 0 && (
        <ul className="plan-files">
          {p.files.map(f => (
            <li key={f.id}>
              <span className={`plan-type plan-type-${f.type === 'pdf' ? 'pdf' : 'word'}`}>{typeLabel(f.type)}</span>
              <a href={fileHref(f)} target="_blank" rel="noopener noreferrer" className="plan-file-link">{f.file_name}</a>
              <span className="plan-file-size">{fmtSize(f.size)}</span>
              <a href={fileHref(f)} target="_blank" rel="noopener noreferrer" className="note-readmore" aria-label={`${f.type === 'pdf' ? 'Ouvrir' : 'Télécharger'} ${f.file_name}`}>{f.type === 'pdf' ? 'ouvrir →' : 'télécharger →'}</a>
              {actions && <button type="button" className="plan-file-del" onClick={() => actions.delFile(p, f)} aria-label={`Retirer ${f.file_name}`} title="Retirer ce fichier">✕</button>}
            </li>
          ))}
        </ul>
      )}
      {n === 0 && <p className="plan-file-name">aucun fichier</p>}
      {actions && (
        <div className="note-item-actions">
          <button type="button" className="btn secondary note-edit" disabled={busy} onClick={() => input.current && input.current.click()}>{busy ? 'envoi en cours…' : '+ ajouter des fichiers'}</button>
          <input ref={input} type="file" multiple accept={ACCEPT} hidden onChange={e => { actions.addFiles(p, e.target.files); e.target.value = ''; }} />
          <button type="button" className="btn danger note-edit" onClick={() => actions.delPlan(p)}>supprimer le plan</button>
        </div>
      )}
    </li>
  );
}
